import { useContext, useEffect, useRef } from 'react';
import { QueryClientContext, useQuery } from '@tanstack/react-query';
import { measureRepositoryQuery } from './performanceTelemetry';
import { repositoryQueryClient } from './queryClient';

export type AsyncState<T> = {
  data: T | undefined;
  loading: boolean;
  error: string | undefined;
  reload: () => void;
};

/**
 * Repository-query adapter backed by TanStack Query. Existing feature modules
 * keep their compact state shape while gaining request de-duplication, shared
 * cache entries, bounded retries, and targeted invalidation.
 *
 * @param load fetcher; it is re-run whenever `key` changes.
 * @param key stable string identifying this request.
 */
export function useAsyncData<T>(
  load: () => Promise<T>,
  key: string,
): AsyncState<T> {
  const queryClient = useContext(QueryClientContext) ?? repositoryQueryClient;
  const loadRef = useRef(load);

  // Effects run in order, so the fetcher is current before the fetch below.
  useEffect(() => {
    loadRef.current = load;
  });

  const query = useQuery({
    queryKey: ['repository', key],
    queryFn: () => measureRepositoryQuery(key, () => loadRef.current()),
    // Mutations are invalidated explicitly; a remounted route still verifies
    // freshness so a component-local generation key cannot resurrect old data.
    staleTime: 0,
    placeholderData: (previous) => previous,
  }, queryClient);

  return {
    data: query.data,
    loading: query.isPending,
    error: query.error?.message,
    reload: () => {
      void query.refetch();
    },
  };
}
